import { motion } from 'framer-motion';
import { ArrowRight, Phone, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DEALERSHIP_INFO } from '../config/dealership';

// McCarthy Motors dealership hero image
const HERO_BG = 'https://customer-assets.emergentagent.com/job_445a52a8-d296-4750-a16f-271931324113/artifacts/2p0vxaiv_IMG_5534.jpeg';

const Hero = () => {
  return (
    <section
      className="relative min-h-screen w-full overflow-hidden flex items-center"
      data-testid="hero-section"
    >
      {/* Background Image - positioned to focus on building/forecourt, not sky */}
      <div className="absolute inset-0">
        <motion.div
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
          className="w-full h-full"
        >
          <img
            src={HERO_BG}
            alt="McCarthy Motors Midleton - Premium Vehicle Dealership"
            className="w-full h-full object-cover object-[center_35%] sm:object-[center_40%]"
            data-testid="hero-background-image"
          />
        </motion.div>
        {/* Overlays for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#000000] via-[#000000]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#000000] via-transparent to-[#000000]/40" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 py-32 lg:py-0">
        <div className="max-w-2xl">
          
          {/* Gold Label */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-8"
          >
            <span className="label-gold" data-testid="hero-subtitle">
              McCarthy Motors Midleton
            </span>
          </motion.div>

          {/* Main Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="font-serif text-4xl sm:text-5xl lg:text-7xl font-medium text-white leading-[1.1] tracking-tight mb-6"
            data-testid="hero-title"
          >
            Quality Vehicles.
            <br />
            <span className="text-[#CCAD60]">Expert Service.</span>
          </motion.h1>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-lg text-zinc-400 max-w-lg leading-relaxed mb-10"
            data-testid="hero-description"
          >
            {DEALERSHIP_INFO.tagline}. Delivering exceptional automotive care 
            with premium pre-owned vehicles in Midleton, Co. Cork.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 mb-16"
          >
            <Link
              to="/cars"
              className="btn-gold inline-flex items-center gap-2"
              data-testid="hero-cta-browse"
            >
              View Our Stock
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/contact"
              className="btn-outline"
              data-testid="hero-cta-contact"
            >
              Get In Touch
            </Link>
          </motion.div>

          {/* Quick Info Bar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1 }}
            className="flex flex-col sm:flex-row gap-6 sm:gap-10"
          >
            <a 
              href={DEALERSHIP_INFO.phoneHref}
              className="flex items-center gap-3 text-zinc-400 hover:text-white transition-colors group"
            >
              <div className="w-10 h-10 flex items-center justify-center border border-[#CCAD60]/20 group-hover:border-[#CCAD60]/50 transition-colors">
                <Phone size={16} className="text-[#CCAD60]" />
              </div>
              <div>
                <p className="text-xs text-zinc-600 uppercase tracking-wider">Call Us</p>
                <p className="text-sm text-white font-medium">{DEALERSHIP_INFO.phone}</p>
              </div>
            </a>
            <a 
              href={DEALERSHIP_INFO.mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-zinc-400 hover:text-white transition-colors group"
            >
              <div className="w-10 h-10 flex items-center justify-center border border-[#CCAD60]/20 group-hover:border-[#CCAD60]/50 transition-colors">
                <MapPin size={16} className="text-[#CCAD60]" />
              </div>
              <div>
                <p className="text-xs text-zinc-600 uppercase tracking-wider">Visit Us</p>
                <p className="text-sm text-white font-medium">Midleton, Co. Cork</p>
              </div>
            </a>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2"
        data-testid="scroll-indicator"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          className="w-px h-12 bg-gradient-to-b from-[#CCAD60] to-transparent"
        />
        <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-600">
          Scroll
        </span>
      </motion.div>

      {/* Decorative Gold Line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#CCAD60]/20 to-transparent" />
    </section>
  );
};

export default Hero;
